import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Container, Typography, Grid, useTheme, Stack } from '@mui/material';
import { Check } from '@mui/icons-material';
import { SectionHeader } from './sectionHeader';
import { SectionBox } from './sectionBox';
import { CtaButton } from './ctaButton';
// Reusable component for simple check badge (checkmark + text)
const CheckBadge = ({ text }) => {
    const theme = useTheme();
    return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: [_jsx(Check, { sx: { color: theme.palette.success.main, fontSize: '18px' } }), _jsx(Typography, { variant: 'body1', sx: { color: theme.palette.success.main }, children: text })] }));
};
// Promo banner component
const PromoBanner = ({ title, subtitle }) => {
    const theme = useTheme();
    return (_jsxs(Box, { sx: {
            backgroundColor: '#e8f5e9',
            borderRadius: '12px',
            p: { xs: 5, md: 6 },
            mb: 8,
            textAlign: 'center',
            border: `1px solid ${theme.palette.success.light}`,
        }, children: [_jsx(Typography, { variant: "h6", sx: {
                    color: theme.palette.success.dark,
                    fontWeight: 600,
                    fontSize: { xs: '16px', md: '18px' },
                    mb: 1,
                }, children: title }), _jsx(Typography, { variant: "body2", sx: {
                    color: theme.palette.text.secondary,
                    fontSize: { xs: '14px', md: '14px' },
                }, children: subtitle })] }));
};
const PricingCard = ({ plan, ctaText, ctaHref, onCtaClick }) => {
    const theme = useTheme();
    const isPopular = plan.isPopular;
    const isCustom = plan.variant === 'custom';
    const showBadge = isPopular || Boolean(plan.badge);
    const badgeText = plan.badge || 'Most Popular';
    const resolvedCtaText = plan.ctaText ?? ctaText;
    const resolvedCtaHref = plan.ctaHref ?? ctaHref;
    // Per-plan CTA overrides bypass the section-level onCtaClick handler so each
    // plan can route to its own destination (e.g. Free → /scoring, Paid → /waitlist).
    const useSectionClickHandler = !plan.ctaHref && !plan.ctaText && Boolean(onCtaClick);
    // Color tokens that flip for the custom (dark concierge) variant
    const cardBg = isCustom
        ? 'linear-gradient(160deg, #0f172a 0%, #1e293b 100%)'
        : theme.palette.background.paper;
    const textPrimary = isCustom ? '#fff' : theme.palette.text.primary;
    const textSecondary = isCustom ? 'rgba(255,255,255,0.7)' : theme.palette.text.secondary;
    const accent = isCustom ? '#14b8a6' : theme.palette.primary.main;
    return (_jsxs(Box, { sx: {
            p: 8,
            background: cardBg,
            border: isCustom
                ? '1px solid rgba(20, 184, 166, 0.25)'
                : isPopular
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
            borderRadius: '12px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: isCustom
                ? '0 0 0 1px rgba(20, 184, 166, 0.15), 0 12px 40px rgba(15, 23, 42, 0.3), 0 0 60px rgba(20, 184, 166, 0.25)'
                : isPopular
                    ? '0 8px 32px rgba(93, 135, 255, 0.18)'
                    : 'none',
            ...(isCustom && {
                animation: 'customCardGlow 3.5s ease-in-out infinite',
                '@keyframes customCardGlow': {
                    '0%, 100%': {
                        boxShadow: '0 0 0 1px rgba(20, 184, 166, 0.15), 0 12px 40px rgba(15, 23, 42, 0.3), 0 0 60px rgba(20, 184, 166, 0.25)',
                    },
                    '50%': {
                        boxShadow: '0 0 0 1px rgba(20, 184, 166, 0.35), 0 12px 50px rgba(15, 23, 42, 0.4), 0 0 90px rgba(20, 184, 166, 0.45)',
                    },
                },
            }),
        }, children: [isCustom && (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                            position: 'absolute',
                            top: -80,
                            right: -80,
                            width: 260,
                            height: 260,
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(20,184,166,0.45) 0%, transparent 65%)',
                            pointerEvents: 'none',
                            animation: 'customGlowPulse 4s ease-in-out infinite',
                            '@keyframes customGlowPulse': {
                                '0%, 100%': { opacity: 0.7, transform: 'scale(1)' },
                                '50%': { opacity: 1, transform: 'scale(1.15)' },
                            },
                        } }), _jsx(Box, { sx: {
                            position: 'absolute',
                            bottom: -100,
                            left: -80,
                            width: 240,
                            height: 240,
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
                            pointerEvents: 'none',
                            animation: 'customGlowPulse2 5s ease-in-out infinite',
                            '@keyframes customGlowPulse2': {
                                '0%, 100%': { opacity: 0.5, transform: 'scale(1.1)' },
                                '50%': { opacity: 0.85, transform: 'scale(1)' },
                            },
                        } })] })), showBadge && (_jsx(Box, { sx: {
                    position: 'absolute',
                    top: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: isCustom ? '#14b8a6' : theme.palette.primary.main,
                    color: '#fff',
                    px: 5,
                    py: 1,
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap',
                    zIndex: 1,
                }, children: badgeText })), _jsx(Typography, { sx: {
                    fontWeight: 700,
                    fontSize: '18px',
                    color: textPrimary,
                    mb: plan.highlight ? 1 : 3,
                    mt: showBadge ? 8 : 0,
                    position: 'relative',
                }, children: plan.name }), plan.highlight && (_jsx(Typography, { sx: {
                    color: accent,
                    fontSize: '16px',
                    fontWeight: 600,
                    mb: 3,
                    position: 'relative',
                }, children: plan.highlight })), _jsx(Typography, { sx: {
                    color: textSecondary,
                    fontSize: '16px',
                    lineHeight: 1.5,
                    mb: 6,
                    minHeight: 60,
                    position: 'relative',
                }, children: plan.description }), _jsxs(Box, { sx: { mb: 2, position: 'relative' }, children: [_jsx(Typography, { component: "span", sx: {
                            fontWeight: 700,
                            fontSize: isCustom ? '28px' : '40px',
                            color: textPrimary,
                            whiteSpace: 'nowrap',
                        }, children: plan.price }), plan.period && (_jsx(Typography, { component: "span", sx: {
                            fontSize: '16px',
                            color: textSecondary,
                        }, children: plan.period }))] }), plan.priceNote && (_jsx(Typography, { sx: {
                    color: textSecondary,
                    fontSize: '12px',
                    mb: 6,
                    position: 'relative',
                }, children: plan.priceNote })), _jsx(Box, { sx: { mb: 6, flex: 1, position: 'relative' }, children: plan.features.map((feature, index) => (_jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }, children: [_jsx(Typography, { sx: { color: isCustom ? accent : theme.palette.text.secondary, fontSize: '16px' }, children: "\u2022" }), _jsx(Typography, { sx: {
                                color: textPrimary,
                                fontSize: '16px',
                            }, children: feature })] }, index))) }), _jsx(CtaButton, { href: useSectionClickHandler ? undefined : resolvedCtaHref, onClick: useSectionClickHandler ? onCtaClick : undefined, variant: "primary", size: "medium", sx: {
                    width: '100%',
                    py: 2.5,
                    position: 'relative',
                    ...(isCustom && {
                        backgroundColor: '#14b8a6',
                        '&:hover': {
                            backgroundColor: '#0d9488',
                        },
                    }),
                }, children: resolvedCtaText }), plan.guaranteeText && (_jsx(Typography, { sx: {
                    color: textSecondary,
                    fontSize: '12px',
                    textAlign: 'center',
                    mt: 2,
                    position: 'relative',
                }, children: plan.guaranteeText }))] }));
};
export const PricingSection = ({ label, title, subtitle, plans, topBadges, promoBanner, ctaText = 'Get Started', ctaHref = '/scoring', onCtaClick, roiHook, }) => {
    const theme = useTheme();
    return (_jsx(SectionBox, { background: theme.palette.background.paper, id: "pricing", children: _jsxs(Container, { maxWidth: "lg", sx: { px: { xs: 4, md: 6 } }, children: [_jsx(SectionHeader, { label: label, title: title, subtitle: subtitle, maxWidth: 600, marginBottom: 6 }), _jsx(Stack, { direction: { xs: 'column', md: 'row' }, spacing: { xs: 2, md: 8 }, justifyContent: "center", alignItems: "center", sx: { mb: 8 }, children: topBadges.map((badge, index) => (_jsx(CheckBadge, { text: badge }, index))) }), promoBanner && (_jsx(PromoBanner, { title: promoBanner.title, subtitle: promoBanner.subtitle })), _jsx(Grid, { container: true, spacing: 6, children: plans.map((plan, index) => (_jsx(Grid, { size: { xs: 12, sm: 6, md: plans.length >= 4 ? 3 : 4 }, children: _jsx(PricingCard, { plan: plan, ctaText: ctaText, ctaHref: ctaHref, onCtaClick: onCtaClick }) }, index))) }), roiHook && (_jsx(Typography, { sx: {
                        mt: 8,
                        textAlign: 'center',
                        color: theme.palette.text.secondary,
                        fontSize: '15px',
                        fontStyle: 'italic',
                        maxWidth: 720,
                        mx: 'auto',
                    }, children: roiHook }))] }) }));
};
